import React from 'react';
import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { QuestionOptionDtoFrontend } from '../../../api/sessionQuestionsApi';

export interface ProfessionPickerProps {
    /** Question option list as it arrives from the backend (`{ key, label }` per row). */
    options: Array<Pick<QuestionOptionDtoFrontend, 'key' | 'label'>>;
    /** Currently-selected profession key (empty string for "not picked yet"). */
    value: string;
    /** Fired with the freshly-picked profession key. Async so callers can await persist. */
    onChange: (key: string) => Promise<void> | void;
    /** Optional label for the wrapping FormControl (e.g. the question text). */
    label?: string;
    /** Shown inside the Select when no value has been picked yet. */
    placeholder?: string;
    /** Disable interaction while a persist is in flight. */
    disabled?: boolean;
    /** Forwarded onto FormControl so the form's red-asterisk styling matches. */
    required?: boolean;
    /** DOM id base — used to link InputLabel to the Select. */
    idBase?: string;
}

/**
 * Reusable profession dropdown. Replaces the old RADIO list of professions
 * (which scrolled forever on small screens) and is also reused inside the
 * FINAL_PROFESSION_CONFIRMATION "add another profession" composite widget.
 * Lives in `fields/` so the same component covers both call sites.
 */
export const ProfessionPicker: React.FC<ProfessionPickerProps> = ({
    options,
    value,
    onChange,
    label,
    placeholder,
    disabled,
    required,
    idBase = 'profession-picker',
}) => {
    const labelId = `${idBase}-label`;

    const handleChange = async (event: SelectChangeEvent<string>) => {
        const next = event.target.value;
        await onChange(next);
    };

    // MUI Select treats the `InputLabel` AS the empty-state placeholder —
    // it floats inside the box when no value is picked, then shrinks to the
    // notched-outline position once a value lands. Adding ALSO a
    // `displayEmpty` + `renderValue` placeholder layers a second piece of
    // text on top of the label and produces the overlapping "Select
    // Profession" / "Profession" collision the user hit on 2026-06-29.
    //
    // Two distinct UX modes:
    //   - `label` provided (typical) → label-as-placeholder, no renderValue
    //     override. Once the worker picks, label shrinks and the chosen
    //     option's text fills the box.
    //   - Only `placeholder` provided (no label) → renderValue handles the
    //     empty state. No InputLabel rendered, so nothing to collide with.
    const hasLabel = Boolean(label);

    return (
        <FormControl fullWidth required={required} disabled={disabled}>
            {hasLabel && <InputLabel id={labelId}>{label}</InputLabel>}
            <Select
                labelId={hasLabel ? labelId : undefined}
                value={value}
                onChange={handleChange}
                label={hasLabel ? label : undefined}
                displayEmpty={!hasLabel && Boolean(placeholder)}
                renderValue={
                    hasLabel
                        ? undefined
                        : (selected) => {
                              if (!selected) {
                                  return placeholder ? (
                                      <span style={{ color: 'rgba(0,0,0,0.42)' }}>
                                          {placeholder}
                                      </span>
                                  ) : '';
                              }
                              const match = options.find((opt) => opt.key === selected);
                              return match?.label ?? selected;
                          }
                }
            >
                {options.map((opt) => (
                    <MenuItem key={opt.key} value={opt.key}>
                        {opt.label}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};
